import {
  Box,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { BackButton, Button, ErrorMessage, Heading, Select } from '../../components/Common';
import { useNavigate } from 'react-router-dom';
import { countryList } from '../../helper';
import { useMessage } from '../../components/Common/Message/MessageContext';
import {
  useGetCityListQuery,
  useGetStateListQuery,
  useCreatePincodeMutation,
  useGetExtraPincodesByCityQuery,
  useDeleteExtraPincodeMutation,
  useUpdateExtraPincodeMutation, // Add this new hook
} from '../../services/Location';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const CreatePincode = () => {
  const { showMessage } = useMessage();
  const navigate = useNavigate();
  const [country, setCountry] = useState(1);
  const [states, setStates] = useState(5);
  const [city, setCity] = useState(null);
  const [pincodeInput, setPincodeInput] = useState('');
  const [pincodes, setPincodes] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingPincodeValue, setEditingPincodeValue] = useState('');
  const [errorMessages, setErrorMessages] = useState({});

  // Dialog state for order association warnings
  const [orderDialog, setOrderDialog] = useState({
    open: false,
    pincode: '',
    orders: [],
    totalOrders: 0,
    action: '', // 'edit' or 'delete'
  });

  const { data: cityLst, error: cityError, isLoading: cityLoading } = useGetCityListQuery(states, { skip: !states });
  const cityList = cityLst?.data;
  const { data: stateLst, error: stateError, isLoading: stateLoading } = useGetStateListQuery();
  const stateList = stateLst?.data;

  const [createPincode] = useCreatePincodeMutation();
  const { data: extraPincodeRes, isLoading: extraPincodeLoading } = useGetExtraPincodesByCityQuery(city, {
    skip: !city,
  });
  const [deleteExtraPincode, { isLoading: deleteLoading }] = useDeleteExtraPincodeMutation();
  const [updateExtraPincode, { isLoading: updateLoading }] = useUpdateExtraPincodeMutation();

  const extraPincodes = extraPincodeRes?.data || [];


  const handleStateChange = (e) => {
    setStates(e.target.value);
    setCity(null);
    setPincodes([]);
  };

  const handleCountryChange = (e) => {
    setCountry(e.target.value);
    setStates(null);
    setCity(null);
    setPincodes([]);
  };

  const handleCityChange = (e) => {
    setCity(e.target.value);
    setPincodes([]);
    setEditingRowId(null);
    setEditingPincodeValue('');
  };

  const validateField = (field, fieldName) => {
    if (!field) {
      setErrorMessages(prevState => ({ ...prevState, [fieldName]: `${fieldName} is a mandatory field.` }));
      return true;
    }
    setErrorMessages(prevState => ({ ...prevState, [fieldName]: '' }));
    return false;
  };

  const handlePincodeInputChange = (e) => {
    const value = e.target.value;
    if (/^\d{0,6}$/.test(value)) {
      setPincodeInput(value);
      if (value.length === 6) {
        setErrorMessages(prev => ({ ...prev, pincodeInput: '' }));
      }
    }
  };

  const handleAddPincode = () => {
    if (pincodeInput.trim() === '') {
      setErrorMessages(prev => ({ ...prev, pincodeInput: 'Pincode cannot be empty' }));
      return;
    }

    if (pincodeInput.trim().length !== 6) {
      setErrorMessages(prev => ({ ...prev, pincodeInput: 'Pincode must be exactly 6 digits' }));
      return;
    }

    if (!pincodes.includes(pincodeInput.trim())) {
      setPincodes([...pincodes, pincodeInput.trim()]);
      setPincodeInput('');
      setErrorMessages(prev => ({ ...prev, pincodeInput: '' }));
    } else {
      setErrorMessages(prev => ({ ...prev, pincodeInput: 'This pincode is already added' }));
    }
  };

  const handleRemovePincode = (pincodeToRemove) => {
    setPincodes(pincodes.filter(pincode => pincode !== pincodeToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddPincode();
    }
  };

  useEffect(() => {
    if (stateList && !stateList.find(s => s.id === states)) {
      const gujarat = stateList.find(s => s.name?.toLowerCase() === 'gujarat');
      if (gujarat) setStates(gujarat.id);
    }
  }, [stateList]);


  const handleDeleteExistingPincode = async (id) => {
    try {
      const res = await deleteExtraPincode(id).unwrap();
      const { status, message } = res || {};
      if (status) {
        showMessage('success', message || 'Pincode deleted successfully');
      } else {
        showMessage('error', message || 'Failed to delete pincode');
      }
    } catch (error) {
      console.error('Failed to delete pincode:', error);

      // Check if error contains order association info
      if (error?.data?.orders && error?.data?.totalOrders) {
        setOrderDialog({
          open: true,
          pincode: error.data.message.match(/\d{6}/)?.[0] || '',
          orders: error.data.orders,
          totalOrders: error.data.totalOrders,
          action: 'delete',
        });
      } else {
        showMessage('error', error?.data?.message || 'Failed to delete pincode');
      }
    }
  };

  const handleStartEditExisting = (row) => {
    setEditingRowId(row.id);
    setEditingPincodeValue(row.pincode);
  };

  const handleCancelEditExisting = () => {
    setEditingRowId(null);
    setEditingPincodeValue('');
  };

  const handleSaveEditExisting = async () => {
    if (!editingRowId) return;

    const newPin = editingPincodeValue?.toString().trim();

    if (!newPin) {
      showMessage('error', 'Pincode cannot be empty');
      return;
    }

    if (newPin.length !== 6 || !/^\d{6}$/.test(newPin)) {
      showMessage('error', 'Pincode must be exactly 6 digits');
      return;
    }

    const currentPincode = extraPincodes.find(p => p.id === editingRowId);

    if (currentPincode && currentPincode.pincode === newPin) {
      setEditingRowId(null);
      setEditingPincodeValue('');
      showMessage('info', 'No changes made');
      return;
    }

    try {
      // ✅ Use the new update mutation which checks orders FIRST
      const response = await updateExtraPincode({
        id: editingRowId,
        pincode: newPin
      }).unwrap();

      const { status, message } = response || {};

      if (status) {
        showMessage('success', message || 'Pincode updated successfully');
        setEditingRowId(null);
        setEditingPincodeValue('');
      } else {
        showMessage('error', message || 'Failed to update pincode');
      }
    } catch (error) {
      console.error('Failed to update pincode:', error);

      // Check if error is due to order association
      if (error?.data?.orders && error?.data?.totalOrders) {
        setOrderDialog({
          open: true,
          pincode: currentPincode.pincode,
          orders: error.data.orders,
          totalOrders: error.data.totalOrders,
          action: 'edit',
        });
        setEditingRowId(null);
        setEditingPincodeValue('');
      } else {
        const errorMessage =
          error?.data?.message ||
          error?.error ||
          'An error occurred while updating pincode';
        showMessage('error', errorMessage);
      }
    }
  };

  const handleFormSubmit = async () => {
    setErrorMessages({});

    if (pincodes.length === 0) {
      setErrorMessages(prev => ({ ...prev, pincodes: 'Please add at least one pincode.' }));
      return;
    } else {
      const invalidPincodes = pincodes.some(p => p.length !== 6);
      if (invalidPincodes) {
        setErrorMessages(prev => ({ ...prev, pincodes: 'All pincodes must be 6 digits' }));
        return;
      } else {
        setErrorMessages(prev => ({ ...prev, pincodes: '' }));
      }
    }

    const countryError = validateField(country, 'country');
    const stateError = validateField(states, 'state');
    const cityError = validateField(city, 'city');

    if (countryError || stateError || cityError) {
      return;
    }

    const payload = {
      country,
      state: states,
      city,
      pincode: pincodes,
    };

    try {
      const response = await createPincode(payload).unwrap();
      const { status, message } = response || {};

      if (status) {
        navigate('/admin');
        showMessage('success', message || 'Pincodes created successfully');
      } else {
        showMessage('error', message || 'Failed to create pincodes');
      }
    } catch (error) {
      console.error('Failed to create pincodes:', error);
      const errorMessage =
        error?.data?.message ||
        error?.error ||
        'An error occurred while creating pincodes';
      showMessage('error', errorMessage);
    }
  };

  const handleCloseOrderDialog = () => {
    setOrderDialog({
      open: false,
      pincode: '',
      orders: [],
      totalOrders: 0,
      action: '',
    });
  };

  return (
    <>
      <Stack flexDirection={'row'} gap={1} mb={1}>
        <BackButton />
        <Heading head='Create Pincode' />
      </Stack>
      <Box className='card_container'>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select
              selectList={countryList}
              labelinput='Country'
              name="country"
              value={country}
              onChange={handleCountryChange}
              disabled={true}
            />
            {errorMessages.country && <ErrorMessage message={errorMessages.country} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select
              selectList={stateLoading ? [] : stateList}
              labelinput='State'
              name="state"
              value={states}
              onChange={handleStateChange}
            />
            {errorMessages.state && <ErrorMessage message={errorMessages.state} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select
              selectList={cityLoading ? [] : cityList}
              labelinput='City'
              name="city"
              value={city}
              onChange={handleCityChange}
            />
            {errorMessages.city && <ErrorMessage message={errorMessages.city} />}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={'2px'}>
              Add Pincodes
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                type='number'
                value={pincodeInput}
                onChange={handlePincodeInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Enter 6-digit pincode"
                inputProps={{ maxLength: 6 }}
                error={!!errorMessages.pincodeInput}
                helperText={errorMessages.pincodeInput}
              />
              <Button
                buttonName="Add"
                onClick={handleAddPincode}
                disabled={!pincodeInput.trim() || pincodeInput.trim().length !== 6}
                sx={{ minWidth: '100px' }}
              />
            </Stack>
            {errorMessages.pincodes && <ErrorMessage message={errorMessages.pincodes} />}
            <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
              {pincodes.map((pincode) => (
                <Chip
                  key={pincode}
                  label={pincode}
                  onDelete={() => handleRemovePincode(pincode)}
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Stack flexDirection="row" gap={1}>
              <Button buttonName="save" onClick={handleFormSubmit} />
              <Button
                buttonName="cancel"
                color="white"
                onClick={() => navigate('/admin')}
              />
            </Stack>
          </Grid>

          {city && (
            <Grid item xs={12}>
              <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={1}>
                Existing Pincodes for Selected City
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Pincode</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {extraPincodeLoading ? (
                      <TableRow>
                        <TableCell colSpan={2}>Loading pincodes...</TableCell>
                      </TableRow>
                    ) : extraPincodes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2}>No pincodes found for this city.</TableCell>
                      </TableRow>
                    ) : (
                      extraPincodes.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            {editingRowId === row.id ? (
                              <TextField
                                size="small"
                                value={editingPincodeValue}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (/^\d{0,6}$/.test(val)) {
                                    setEditingPincodeValue(val);
                                  }
                                }}
                                placeholder="Enter 6-digit pincode"
                                type="number"
                                inputProps={{ maxLength: 6 }}
                              />
                            ) : (
                              row.pincode
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {editingRowId === row.id ? (
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                  buttonName="Save"
                                  onClick={handleSaveEditExisting}
                                  disabled={!editingPincodeValue || editingPincodeValue.length !== 6 || updateLoading}
                                />
                                <Button
                                  buttonName="Cancel"
                                  color="white"
                                  onClick={handleCancelEditExisting}
                                />
                              </Stack>
                            ) : (
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <IconButton
                                  size="small"
                                  onClick={() => handleStartEditExisting(row)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteExistingPincode(row.id)}
                                  disabled={deleteLoading}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Order Association Warning Dialog */}
      <Dialog
        open={orderDialog.open}
        onClose={handleCloseOrderDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Cannot {orderDialog.action === 'delete' ? 'Delete' : 'Edit'} Pincode
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Pincode <strong>{orderDialog.pincode}</strong> is associated with{' '}
            <strong>{orderDialog.totalOrders}</strong> order(s) and cannot be{' '}
            {orderDialog.action === 'delete' ? 'deleted' : 'edited'}.
          </DialogContentText>

          {orderDialog.orders && orderDialog.orders.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Associated Orders:
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order Number</TableCell>
                      <TableCell>Order Date</TableCell>
                      <TableCell>Pincode</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderDialog.orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>{order.orderNumber}</TableCell>
                        <TableCell>
                          {order.orderDate ? new Date(order.orderDate).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>{order.zip}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {orderDialog.totalOrders > orderDialog.orders.length && (
                <Typography variant="caption" color="text.secondary" mt={1}>
                  Showing {orderDialog.orders.length} of {orderDialog.totalOrders} total orders
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button buttonName="Close" onClick={handleCloseOrderDialog} />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreatePincode;